import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Phone, ShieldCheck, Timer, Sparkles, HeartPulse, ChevronRight } from "lucide-react";
import { CONTACT, THEME, servicesOf, doctorFor } from "@/data/site";
import { DoctorProfile } from "@/components/common/DoctorProfile";
import { ServiceCard } from "@/components/common/ServiceCard";
import { AppointmentForm } from "@/components/common/AppointmentForm";
import { SectionHead, Reveal } from "@/components/common/Reveal";
import { FaqList } from "@/components/common/FaqList";

const WHY = {
  surgery: [
    { icon: Sparkles, hi: "छोटा चीरा, कम निशान", en: "Minimally invasive keyhole technique" },
    { icon: Timer, hi: "जल्दी छुट्टी व रिकवरी", en: "Most patients go home in 1–2 days" },
    { icon: ShieldCheck, hi: "सुरक्षित व स्टेराइल OT", en: "Strict sterile protocols" },
    { icon: HeartPulse, hi: "ऑपरेशन के बाद पूरी देखभाल", en: "Dressing, follow-up & diet guidance" },
  ],
  eye: [
    { icon: Sparkles, hi: "बिना टाँका फेको सर्जरी", en: "Topical, stitchless, needle-free phaco" },
    { icon: ShieldCheck, hi: "कम्प्यूटरीकृत जाँच", en: "Modern diagnostics for every patient" },
    { icon: Timer, hi: "उसी दिन घर", en: "Day-care cataract surgery" },
    { icon: HeartPulse, hi: "हर बृहस्पतिवार नि:शुल्क जाँच", en: "Free eye check-up every Thursday" },
  ],
};

const Hero = ({ t, d, count }) => (
  <section data-testid={`dept-hero-${t.key}`} className={`grain relative overflow-hidden ${t.key === "surgery" ? "bg-surg-deep" : "bg-maroon-ink"}`}>
    <img src={t.image} alt="" className="kenburns absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="container-x relative grid items-end gap-10 py-14 md:py-20 lg:grid-cols-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7 }} className="lg:col-span-7">
        <nav className="font-en flex items-center gap-1 text-xs text-white/60"><Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" /><span className="text-white">{t.labelEn}</span></nav>
        <p className="eyebrow mt-6 text-amber-300">{t.deptHi} · {count} सेवाएँ</p>
        <h1 className="font-display mt-4 text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">{t.heroHi}</h1>
        <p className="font-en mt-5 max-w-xl text-white/75 md:text-lg">{t.heroEn}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#appointment" data-testid={`dept-book-${t.key}`} className="btn btn-light"><CalendarCheck className="h-4 w-4" /> अपॉइंटमेंट बुक करें</a>
          <a href={`tel:${CONTACT.phoneDial}`} className="btn btn-outline text-white"><Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}</a>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .15 }} className="lg:col-span-5">
        <a href="#doctor" className="flex items-center gap-4 rounded-3xl bg-white/95 p-4 shadow-2xl backdrop-blur" data-testid={`dept-hero-doctor-${t.key}`}>
          <img src={d.photo} alt={d.nameEn} className="h-20 w-20 rounded-2xl object-cover object-top" />
          <div className="min-w-0">
            <p className={`eyebrow ${t.text}`}>विभागाध्यक्ष</p>
            <p className="font-display text-2xl text-ink">{d.nameHi}</p>
            <p className="font-en truncate text-sm text-muted2">{d.degreesEn}</p>
          </div>
        </a>
      </motion.div>
    </div>
  </section>
);

const Why = ({ t }) => (
  <section className="border-b border-line bg-white">
    <div className="container-x grid gap-px py-2 sm:grid-cols-2 lg:grid-cols-4">
      {WHY[t.key].map((w, i) => (
        <Reveal key={w.hi} delay={i * 0.06}>
          <div className="flex items-start gap-3 p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.tint} ${t.text}`}><w.icon className="h-5 w-5" /></span>
            <div><p className="font-semibold text-ink">{w.hi}</p><p className="font-en text-xs text-muted2">{w.en}</p></div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function DeptPage({ dept }) {
  const t = THEME[dept];
  const d = doctorFor(dept);
  const list = servicesOf(dept);
  const faqs = list.flatMap((s) => s.faq.slice(0, 1)).slice(0, 6);
  return (
    <div data-testid={`dept-page-${dept}`}>
      <Hero t={t} d={d} count={list.length} />
      <Why t={t} />
      <section id="dept-services" className="py-20 md:py-24">
        <div className="container-x">
          <SectionHead tone={t.text} eyebrow={`${t.labelHi} · ${t.labelEn}`} title={dept === "surgery" ? "सभी सर्जरी सेवाएँ" : "सभी नेत्र सेवाएँ"} sub="Tap a service to read symptoms, treatment steps, benefits and FAQs." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
          </div>
        </div>
      </section>
      <div id="doctor"><DoctorProfile d={d} flip showDeptLink={false} /></div>
      <section className="py-20 md:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead tone={t.text} eyebrow="सवाल-जवाब · FAQ" title="अक्सर पूछे जाने वाले सवाल" /></div>
          <div className="lg:col-span-8"><FaqList faqs={faqs} tid={`dept-faq-${dept}`} /></div>
        </div>
      </section>
      <AppointmentForm defaultDept={dept} />
    </div>
  );
}
